import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { GraduationCap } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { api, errMessage } from "../lib/api";

// Parent side of school integration: link a child to a classroom with a class
// code (creates a revocable, participation-only access grant for the teacher).
export const SchoolPanel = ({ child }) => {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState([]);
  const [code, setCode] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/school/children/${child.child_id}/links`).then(({ data }) => setLinks(data.links)).catch(() => setLinks([]));
  }, [child.child_id]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const lookup = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.get(`/school/class-link/${encodeURIComponent(code.trim())}`);
      setPreview(data);
    } catch (err) {
      setPreview(null);
      toast.error(errMessage(err));
    }
  };

  const link = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/school/class-link", { join_code: code.trim(), child_id: child.child_id });
      toast.success(data.already ? "Already linked to this classroom" : `Linked to ${data.classroom_name}`);
      setPreview(null);
      setCode("");
      load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (accessId) => {
    try {
      await api.delete(`/school/children/${child.child_id}/links/${accessId}`);
      toast.success("School access revoked immediately");
      load();
    } catch (err) {
      toast.error(errMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full rounded-lg gap-2 text-muted-foreground" data-testid={`school-panel-${child.child_id}`}>
          <GraduationCap className="h-4 w-4" aria-hidden="true" /> School
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="school-dialog">
        <DialogHeader>
          <DialogTitle>School classrooms for {child.display_name}</DialogTitle>
          <DialogDescription>
            Link {child.display_name} to a classroom with the class code from their teacher. Teachers only ever
            see participation counts — never chats, stories, recordings or professional notes. You can revoke anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {links.length > 0 && (
            <div className="space-y-2" data-testid="school-links-list">
              {links.map((l) => (
                <div key={l.access_id} className="flex items-center justify-between gap-2 bg-accent rounded-xl px-3 py-2" data-testid={`school-link-${l.access_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{l.classroom_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{l.school_name} · until {new Date(l.expires_at).toLocaleDateString()}</p>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-lg h-8 shrink-0 text-destructive"
                    onClick={() => revoke(l.access_id)} data-testid={`school-link-revoke-${l.access_id}`}>
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={lookup} className="flex gap-2">
            <Input required placeholder="Class code (e.g. CLS-A1B2C3)" value={code}
              onChange={(e) => { setCode(e.target.value); setPreview(null); }}
              className="h-10" data-testid="school-code-input" />
            <Button type="submit" variant="outline" className="rounded-lg shrink-0" data-testid="school-code-lookup-button">
              Look up
            </Button>
          </form>

          {preview && (
            <div className="rounded-xl bg-accent p-4 space-y-3" data-testid="school-lookup-preview">
              <p className="text-sm font-semibold">{preview.classroom_name} · ages {preview.age_band}</p>
              <p className="text-xs text-muted-foreground">{preview.school_name} — teacher {preview.teacher_name}</p>
              <Button onClick={link} disabled={busy} className="w-full rounded-lg" data-testid="school-lookup-confirm-button">
                {busy ? "Linking…" : `Link ${child.display_name} to this classroom`}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
